import { useState } from "react";
import { Button, FormControl, FormLabel, Input, Text, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { extractFormFieldError, useChangePasswordMutation } from "../hooks/useSettingsMutations";

export function ChangePasswordSection() {
  const [t] = useTranslation();
  const [oldPassword, setOldPassword] = useState("");
  const [oldPasswordError, setOldPasswordError] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { mutate, isPending } = useChangePasswordMutation();

  const passwordsMatch = newPassword === confirmPassword;
  const isValid =
    oldPassword.length > 0 && newPassword.length >= 6 && passwordsMatch;

  const handleSave = () => {
    if (isValid) {
      mutate(
        { oldPassword, newPassword },
        {
          onSuccess: () => {
            setOldPassword("");
            setOldPasswordError("");
            setNewPassword("");
            setConfirmPassword("");
          },
          onError: (error: unknown) => {
            setOldPasswordError(extractFormFieldError(error, "old_password_hash") || "");
          },
        },
      );
    }
  };

  return (
    <VStack spacing={5} align="stretch">
      <Text fontSize="xl" fontFamily="heading" fontWeight={600} color="dune.dune">
        {t("screens.settings.changePassword")}
      </Text>

      <FormControl>
        <FormLabel fontSize="sm" color="transparent.white.60">
          {t("screens.settings.currentPassword")}
        </FormLabel>
        <Input
          fontSize="16px"
          type="password"
          value={oldPassword}
          onChange={(e) => {
            setOldPassword(e.target.value);
            setOldPasswordError("");
          }}
          placeholder={t("screens.settings.currentPasswordPlaceholder")}
        />
        {oldPasswordError && (
          <Text fontSize="xs" color="red.300" mt={1}>
            {oldPasswordError}
          </Text>
        )}
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm" color="transparent.white.60">
          {t("screens.settings.newPassword")}
        </FormLabel>
        <Input
          fontSize="16px"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder={t("screens.settings.newPasswordPlaceholder")}
        />
        <Text fontSize="xs" color="transparent.white.30" mt={1}>
          {t("screens.settings.passwordMinLength")}
        </Text>
      </FormControl>

      <FormControl>
        <FormLabel fontSize="sm" color="transparent.white.60">
          {t("screens.settings.confirmNewPassword")}
        </FormLabel>
        <Input
          fontSize="16px"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder={t("screens.settings.confirmPasswordPlaceholder")}
        />
        {confirmPassword && !passwordsMatch && (
          <Text fontSize="xs" color="red.300" mt={1}>
            {t("screens.settings.passwordsMismatch")}
          </Text>
        )}
      </FormControl>

      <Button
        variant="primary"
        size="sm"
        onClick={handleSave}
        isLoading={isPending}
        isDisabled={!isValid || isPending}
      >
        {t("screens.settings.changePassword")}
      </Button>
    </VStack>
  );
}
